import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { CustomerService } from "@/services/features/CustomerService";
import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";
import { formatDurationShort } from "@/utils/time";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

export const useResponseTime = (
  storeIds: number[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  areaIds?: number[],
): AnalyticsCardData => {
  const areaIdsKey = areaIds?.join(",") ?? "";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawData, setRawData] = useState<{
    avgSeconds: number;
    pillText: string;
    isPositive: boolean;
  } | null>(null);
  const beginRequest = useLatestRequestGuard();

  useEffect(() => {
    const isCurrent = beginRequest();
    const ids = Array.isArray(storeIds) ? storeIds : storeIds != null ? [storeIds] : [];
    if (ids.length === 0 || !dateRange) {
      setRawData(null);
      return;
    }

    const fetchResponseTime = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const customerService = CustomerService.getInstance();

        const params: any = {
          storeIds: ids,
          startDate: storePerformanceService.formatDate(dateRange.start, browserTimezone),
          endDate: storePerformanceService.formatDate(dateRange.end, browserTimezone),
          requestStatus: "COMPLETED",
        };
        if (deviceType && deviceType !== "all") params.deviceType = deviceType;
        if (areaIdsKey) params.areaIds = areaIdsKey.split(",").map(Number);

        const completedResponse = await analyticsService.getRequestAnalytics(params);

        const completedCount = completedResponse?.data?.totalRequests ?? 0;
        const totalTimeSeconds = completedResponse?.data?.totalTimeTakenSeconds ?? 0;

        if (completedCount <= 0) {
          if (isCurrent()) setRawData(null);
          return;
        }

        const avgSeconds = totalTimeSeconds / completedCount;

        // Pull store goal to show target response time and positivity (only when exactly one store selected)
        let pillText = "";
        let isPositive = true;
        try {
          if (ids.length === 1) {
            const goalResponse = await customerService.getStoreGoal(ids[0]);
            if (goalResponse.success) {
              const timeGoalSeconds = goalResponse.data?.timeGoal || 60;
              pillText = `Goal ${timeGoalSeconds.toFixed(0)} seconds`;
              isPositive = timeGoalSeconds >= avgSeconds;
            }
          }
        } catch (error) {
          console.error("Error fetching store goal:", error);
        }

        if (isCurrent()) setRawData({ avgSeconds, pillText, isPositive });
      } catch (error) {
        console.error("Error fetching response time:", error);
        if (isCurrent()) setRawData(null);
      }
    };

    fetchResponseTime();
  }, [
    Array.isArray(storeIds) ? storeIds.join(",") : (storeIds ?? null),
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    deviceType,
    areaIdsKey,
  ]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
      };
    }

    return {
      previousValue: null,
      value: formatDurationShort(rawData.avgSeconds, currentLanguage),
      subtitle: i18n.t("common.analytics.averageTimeToFulfillRequest"),
      isPositive: rawData.isPositive,
      pillText: rawData.pillText,
      rawValue: rawData.avgSeconds,
    };
  }, [rawData, currentLanguage]);
};
